import { apiCall } from "@/common/api/ApiResult.model";
import { EmployeesHelper } from "@/common/entities/helpers";
import { InformationType, InfoTypeHelper, ListResult } from "@/common/models";
import { SharedState } from "@/common/state";
import { indexBy, isNotEmpty, path, reduce_and_merge_by_fn } from "@/fp";
import { cord, formatInput, gql2 } from "@/gql/lib";
import { ActionEnum, AxisType, QueryTier1countArgs } from "@/gql/types";
import { to_query_params } from "@/utils/url";
import axios from "axios";
import { RiskyUsers, RiskyUsersPolicyL0StatusItem } from "./model";
import { get_l0_labels_with_policies } from "@/common/api/labels.api";
import moment from "moment";

const mapper_count_reducer = reduce_and_merge_by_fn<any, "id">("id", (a, b) => {
  return {
    ...a,
    // @ts-ignore
    labels: [...a.labels, ...b.labels],
    count: a.count + b.count,
  };
});

export const get_notified_users_approximation_count = gql2<
  void,
  Record<string, number>
>("risky-users.count-approximation", (_, { allInfoTypes }) => {
  const { from, type } = SharedState.get_last_week_time_frame();
  const q: QueryTier1countArgs = {
    params: {
      coordinate: {
        x: cord(AxisType.EmployeeSenderType),
        y: {
          type: AxisType.ContentLabel,
          values: SharedState.virtualLabels.value
            .filter((f) =>
              // @ts-ignore
              ["Low"].includes(f.sensitivityLevel)
            )
            .map((d) => d.id),
        },
        z: {
          type,
          values: [from],
        },
      },
      action: ActionEnum.List,
    },
  };
  return {
    query: `{
      tier1count(params:${formatInput(q.params)}){
        y{value}
        uniqs
      }
    }`,
    mapper: (res) => {
      const result = mapper_count_reducer(
        res.tier1count.map((item: any) => {
          const { y, uniqs } = item;
          const label = SharedState.virtualLabels.value.find(
            (d) => d.id === y.value
          ) as InformationType;
          const parent_label = allInfoTypes[label.parentId as string];
          return {
            id: parent_label.id,
            name: parent_label.labelName,
            count: uniqs,
            labels: [y.value],
          };
        })
      ).reduce((acc, d) => {
        acc[d.id] = d.count;
        return acc;
      }, {} as Record<string, number>);
      console.log("risky-users.count-approximation : ", result);
      return result;
    },
  };
});

/**
 * @note - due to how and when when run the Risky users  calculation
 * we don't support "current week" results. only "last week" -> month ....
 * */
export const get_risky_users_count_for_l0 = apiCall<
  { l0_id: string; date: string; frame: "Month" | "Week" },
  { id: string; count: number }
>("risky-users.for-l0", async ({ l0_id, date, frame }) => {
  if (frame == "Week") {
    // we will set the date to Last Week no matter what (see explanation in the function description)
    date = SharedState.get_last_week_time_frame().from;
  }

  const fixed_date = moment(date);
  console.log(
    "get_risky_users_count_for_l0 , fixed_date::",
    fixed_date.format("YYYY-MM-DD")
  );

  const qparams = {
    infoTypeCategoryId: l0_id,
    useCount: true,
    asOfDate: fixed_date.format("YYYY-MM-DD") + "T00:00:00.000Z",
    isScopeMonthly: frame == "Month",
    limit: 100,
    descentSortByScore: true,
  };

  const res = await axios.get(
    "/extproxy/integrations/risky-users?" + to_query_params(qparams)
  );

  return {
    success: true,
    value: {
      id: l0_id,
      count: res.data.value,
    },
  };
});

/**
 * @note - due to how and when when run the Risky users  calculation
 * we don't support "current week" results. only "last week" -> month ....
 * */
export const get_risky_users_for_l0 = apiCall<
  { l0_id: string; date: string; frame: "Month" | "Week"; cursor: string },
  ListResult<RiskyUsers>
>("risky-users.for-l0", async ({ l0_id, date, cursor: old_cursor, frame }) => {
  let qparams;
  if (old_cursor) {
    qparams = {
      cursor: old_cursor,
    };
  } else {
    if (frame == "Week") {
      // we will set the date to Last Week no matter what (see explanation in the function description)
      date = SharedState.get_last_week_time_frame().from;
    }
    const fixed_date = moment(date);
    qparams = {
      infoTypeCategoryId: l0_id,
      // 2022-10-13T00:00:00.000Z
      asOfDate: fixed_date.format("YYYY-MM-DD") + "T00:00:00.000Z",
      isScopeMonthly: frame == "Month",
      limit: 100,
      descentSortByScore: true,
    };
  }

  const res = await axios.get(
    "/extproxy/integrations/risky-users?" + to_query_params(qparams)
  );

  const { cursor = "", value } = res.data;

  const employee_ids: string[] = (value.riskyUsers || []).map(
    (item: any) => item.entityId
  );
  const employee_deps_map = await EmployeesHelper.get_employees_departments_name_async(
    employee_ids
  );
  return {
    success: true,
    value: {
      cursor,
      objects: value.riskyUsers.map((val: any) => {
        return {
          id: val.entityId,
          name: val.entityName,
          score: val.score,
          isTrusted: val.isTrusted,
          department: employee_deps_map[val.entityId],
        } as RiskyUsers;
      }),
    },
  };
});

// detect_2p detect_5p detect_10p detect_20p
export const toggle_l0_risky_user_active_status = apiCall<
  { id: string; activate: boolean; selected_percentage: number },
  boolean
>(
  "risky-users.change-activate-for-l0",
  async ({ selected_percentage, activate, id }) => {
    const policy_id = activate
      ? `detect_alert_${selected_percentage}`
      : `detect_${selected_percentage}`;
    const payload = [
      {
        id,
        policySection: {
          riskyUser: {
            policyValue: {
              id: policy_id,
            },
          },
        },
      },
    ];

    await axios.patch("/label-api", payload);
    return {
      success: true,
      value: true,
    };
  }
);

export const get_risky_users_status_list = apiCall<
  void,
  RiskyUsersPolicyL0StatusItem[]
>("risky-users.status-list", async () => {
  let last_send_date = "";
  const res_send_date = await get_next_send_date();
  if (res_send_date.success) {
    last_send_date = res_send_date.value;
  }
  const res = await get_l0_labels_with_policies();
  if (res.success) {
    const fresh_l0 = indexBy<InformationType>("id")(res.value);
    console.log("fresh_l0: ", fresh_l0);
    const riskyUser_policy = InfoTypeHelper.get_policy("riskyUser");
    const get_percentage = (val: string): number => {
      if (val) {
        return +val.split("_").reverse()[0];
      }
      return 2;
    };
    return {
      success: true,
      value: SharedState.l0InfoTypes.value
        .filter((f) => f.id !== SharedState.default_label_id.value)
        .map((d) => {
          const policy = riskyUser_policy(fresh_l0[d.id]);
          return {
            id: d.id,
            name: d.labelName,
            order: d.l0Order,
            next_send_date: last_send_date,
            detect_percentage: get_percentage(policy),
            // @ts-ignore *  */
            is_active: isNotEmpty(policy) && policy.includes("alert"),
          } as RiskyUsersPolicyL0StatusItem;
        })
        // @ts-ignore
        .sort((a, b) => a.order - b.order),
    };
  } else {
    return {
      success: true,
      value: [],
    };
  }
});

const get_next_send_date_from_path = path(
  "value.maintenanceEntities.$0.nextApproximateUpdateAt",
  () => "?"
);

export const get_next_send_date = apiCall<void, string>(
  "risky-users.next-send-date",
  async () => {
    const qparams = to_query_params({
      scenarioId: "riskyUsers",
      entityType: "org",
      useOrgAsEntity: true,
    });
    const res = await axios.get(
      "/extproxy/integrations/maintenance-entities?" + qparams
    );

    return {
      success: true,
      value: get_next_send_date_from_path(res.data),
    };
  }
);
